// Wave F (April 2026) — useViewPreference React hook.
//
// Fetches the user's resolved preference for a surface (cascade:
// user → role → null), parses it, and exposes a setter that
// persists back via PUT. Caller passes a zod-like baseline +
// validator so the hook returns a typed, validated shape that
// components can render without further parsing.
//
// Usage:
//   const { value, setValue, source, isLoading } =
//     useViewPreference("household_detail", DEFAULT_LAYOUT, isHouseholdLayout);

import { useCallback, useEffect, useState } from "react";

import { api, ApiError } from "../api";

export interface UseViewPreferenceResult<T> {
  /** The resolved preference value. Always falls back to baseline
   *  while loading or when no row exists. */
  value: T;
  /** "user" — saved by this user
   *  "role" — inherited from role default
   *  "baseline" — no row, hardcoded fallback */
  source: "user" | "role" | "baseline";
  isLoading: boolean;
  loadError: string | null;
  /** Persist a new value via PUT. Optimistic — value updates
   *  locally immediately; reverts on error. */
  setValue: (next: T) => Promise<void>;
  /** Clear the user's row → fall back to role default → baseline. */
  clear: () => Promise<void>;
  /** Last save error, null when the save succeeded or none yet. */
  saveError: string | null;
}

/**
 * Generic hook for managing a per-user view preference for a
 * specific surface. Caller supplies:
 *   - the surface key
 *   - the baseline value (used when no preference exists)
 *   - a parser/validator that converts the persisted JSON string
 *     into the typed shape (returns null on malformed → falls back
 *     to baseline)
 */
export function useViewPreference<T>(
  surfaceKey: string,
  baseline: T,
  parse: (json: string) => T | null,
): UseViewPreferenceResult<T> {
  const [value, setValueState] = useState<T>(baseline);
  const [source, setSource] = useState<"user" | "role" | "baseline">(
    "baseline",
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Fetch on mount + when the surface key changes.
  // PR #7 — also re-fetch when Ask Stew dispatches the
  // 'stew:view-preference-changed' custom event (which fires after
  // an apply OR an undo of a customize_view action). This is what
  // makes the household page re-render with the new layout the
  // instant the user clicks Apply, without a full page reload.
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = () => {
      setIsLoading(true);
      setLoadError(null);
      api
        .getViewPreference(surfaceKey)
        .then((r) => {
          if (cancelled) return;
          setSource(r.source);
          if (r.preferences_json) {
            const parsed = parse(r.preferences_json);
            if (parsed) {
              setValueState(parsed);
            } else {
              console.warn(
                `useViewPreference(${surfaceKey}): parse returned null — falling back to baseline`,
              );
              setValueState(baseline);
              setSource("baseline");
            }
          } else {
            setValueState(baseline);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          setLoadError(err instanceof ApiError ? err.message : "Couldn't load");
          setValueState(baseline);
          setSource("baseline");
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    };
    fetchOnce();

    // PR #7 live-refresh listener.
    function onStewChange(e: Event) {
      const detail = (e as CustomEvent<{ surface_key?: string }>).detail;
      if (!detail || detail.surface_key !== surfaceKey) return;
      fetchOnce();
    }
    window.addEventListener(
      "stew:view-preference-changed",
      onStewChange as EventListener,
    );
    return () => {
      cancelled = true;
      window.removeEventListener(
        "stew:view-preference-changed",
        onStewChange as EventListener,
      );
    };
    // baseline is intentionally not a dep — assumed stable per the
    // caller's component lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surfaceKey, parse]);

  const setValue = useCallback(
    async (next: T) => {
      const previous = value;
      const previousSource = source;
      // Optimistic update.
      setValueState(next);
      setSource("user");
      setSaveError(null);
      try {
        await api.setViewPreference(surfaceKey, JSON.stringify(next));
      } catch (err) {
        // Revert on error.
        setValueState(previous);
        setSource(previousSource);
        setSaveError(err instanceof ApiError ? err.message : "Couldn't save");
      }
    },
    [surfaceKey, source, value],
  );

  const clear = useCallback(async () => {
    setSaveError(null);
    try {
      await api.clearViewPreference(surfaceKey);
      // Refetch to get the role default if any.
      const r = await api.getViewPreference(surfaceKey);
      setSource(r.source);
      if (r.preferences_json) {
        const parsed = parse(r.preferences_json);
        setValueState(parsed ?? baseline);
      } else {
        setValueState(baseline);
      }
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Couldn't clear");
    }
    // baseline stable; same eslint-disable rationale as above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surfaceKey, parse]);

  return {
    value,
    source,
    isLoading,
    loadError,
    setValue,
    clear,
    saveError,
  };
}

// ZoneLayout shape + parser/reconciler live in ./zone-layout (so
// unit tests can import them without pulling in `../api` and its
// DOM-fetch types — see tests/web/use-view-preference.test.ts).
export {
  isZoneLayout,
  parseZoneLayout,
  reconcileZoneLayout,
  type ZoneLayout,
} from "./zone-layout";
